import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
  generalReport: [],
  trackingMetrics: [],
  trackingSummary: {},
  homeVisitReport: [],
  opdReport: [],
  opdConsultants: [],
  opdConversions: {},
  sessionReport: null,
  caseManagementReport: [],
  feedbackReport: null,
  individualClientFileReport: null,
  exerciseMonthlyReport: null,
  adlRecordReport: null,
  isLoading: false,
  error: null,
};

/* ==============================
   ASYNC THUNKS
 ============================== */

export const fetchGeneralReport = createAsyncThunk(
  'reports/fetchGeneralReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/general', { params: filters });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch general report'
      );
    }
  }
);

export const fetchTrackingMetrics = createAsyncThunk(
  'reports/fetchTrackingMetrics',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/tracking-metrics', { params: filters });
      return response.data; // Includes 'data' (metrics table) and 'summary' (cards)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch tracking metrics'
      );
    }
  }
);

export const fetchHomeVisitReport = createAsyncThunk(
  'reports/fetchHomeVisitReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/home-visits', { params: filters });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch home visit report'
      );
    }
  }
);

export const fetchOPDReport = createAsyncThunk(
  'reports/fetchOPDReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/opd', { params: filters });
      return response.data; // Includes 'data' (summary) and 'consultants' (breakdown)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch OPD report'
      );
    }
  }
);

export const fetchSessionReport = createAsyncThunk(
  'reports/fetchSessionReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/sessions', { params: filters });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch session report'
      );
    }
  }
);

export const fetchCaseManagementReport = createAsyncThunk(
  'reports/fetchCaseManagementReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/case-management', { params: filters });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch case management report'
      );
    }
  }
);

export const fetchFeedbackReport = createAsyncThunk(
  'reports/fetchFeedbackReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/feedback', { params: filters });
      return response.data; // { respondents, sections, finalComments, period, total_forms }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch feedback report'
      );
    }
  }
);

export const fetchIndividualClientFileReport = createAsyncThunk(
  'reports/fetchIndividualClientFileReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/individual-client-file', { params: filters });
      return response.data; // { patient, icf, progressNotes, familyMeetings, transition, caseManagement, leapConsent, irp2, sessions, sources }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch individual client file report'
      );
    }
  }
);

export const fetchExerciseMonthlyReport = createAsyncThunk(
  'reports/fetchExerciseMonthlyReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/exercise-monthly', { params: filters });
      return response.data; // { patient, clientInfo, performance, activitySummary, weeklyProgress, outcome, hasData }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch exercise monthly report'
      );
    }
  }
);

export const fetchADLRecordReport = createAsyncThunk(
  'reports/fetchADLRecordReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/adl-record', { params: filters });
      return response.data; // { patient, info, activities, tables, totalDates, hasData }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch ADL record report'
      );
    }
  }
);

/* ==============================
   SLICE
 ============================== */

const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearReportsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // General Report
      .addCase(fetchGeneralReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGeneralReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.generalReport = action.payload;
      })
      .addCase(fetchGeneralReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Tracking Metrics
      .addCase(fetchTrackingMetrics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrackingMetrics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.trackingMetrics = action.payload.data;
        state.trackingSummary = action.payload.summary;
      })
      .addCase(fetchTrackingMetrics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Home Visit Report
      .addCase(fetchHomeVisitReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeVisitReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.homeVisitReport = action.payload.data;
        state.homeVisitYTD = action.payload.ytd_total;
      })
      .addCase(fetchHomeVisitReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // OPD Report
      .addCase(fetchOPDReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOPDReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.opdReport = action.payload.data;
        state.opdConsultants = action.payload.consultants;
        state.opdConversions = action.payload.conversions;
      })
      .addCase(fetchOPDReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Session Report
      .addCase(fetchSessionReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSessionReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionReport = action.payload;
      })
      .addCase(fetchSessionReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Case Management Report
      .addCase(fetchCaseManagementReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCaseManagementReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.caseManagementReport = action.payload;
      })
      .addCase(fetchCaseManagementReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Feedback Report
      .addCase(fetchFeedbackReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeedbackReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.feedbackReport = action.payload;
      })
      .addCase(fetchFeedbackReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Individual Client File Report
      .addCase(fetchIndividualClientFileReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIndividualClientFileReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.individualClientFileReport = action.payload;
      })
      .addCase(fetchIndividualClientFileReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.individualClientFileReport = null;
      })

      // Exercise Monthly Report
      .addCase(fetchExerciseMonthlyReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExerciseMonthlyReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.exerciseMonthlyReport = action.payload;
      })
      .addCase(fetchExerciseMonthlyReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.exerciseMonthlyReport = null;
      })

      // ADL Record Report
      .addCase(fetchADLRecordReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchADLRecordReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adlRecordReport = action.payload;
      })
      .addCase(fetchADLRecordReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.adlRecordReport = null;
      });
  },
});

export const { clearReportsError } = reportSlice.actions;

export default reportSlice.reducer;
